import { Building2 } from 'lucide-react';
import { btns } from '../components/buttons';
import { setActingFirmId } from './advisorActingFirm';
import type { AdvisorFirmChoice } from './types';

/**
 * "Which firm are you working as?" — the whole of steward-app#983's UI.
 *
 * Rendered ONLY when `GET /advisor/firms` answered with `firmChoice`, which it does when
 * the façade could not place this request at ONE of the caller's own firms. Every advisor
 * in production today is single-firm and never sees this component; the first person who
 * will is our own operator, staff at RIPL and at the internal demo firm docs/30 T6 puts in
 * prod.
 *
 * ⚠️ **The copy must not assert how many firms the reader has** (raised in this PR's code
 * review). The usual reason for this screen is "you are staff at two", but it is not the
 * only one: a firm chosen in this tab that later stops matching — a membership deactivated,
 * a firm de-configured — also lands here, with a list of ONE. "You are on the team at more
 * than one firm" would then be a sentence the product knows to be false, told to someone it
 * is already asking a favour of. The question is which firm this is, not how many there
 * are, so the copy asks exactly that.
 *
 * ⚠️ **The list is the SERVER's answer, rendered as given.** This component invents no
 * option, remembers no option, and merges nothing in: pressing a button records that firm
 * id for this tab, and every subsequent read is re-validated against the caller's own
 * memberships by the façade (`advisorFirmContext` — the field may only narrow). Nothing
 * here is a permission decision, and a person who edited the stored value by hand would get
 * the same question back rather than a firm that is not theirs.
 *
 * ⚠️ **Shaped like `EmptyState` on purpose, and deliberately not built on it.** The
 * wrapper, icon and paragraph classes are that component's, so the two read as one family,
 * but `EmptyState` takes one primary action and one secondary and this takes N firms. The
 * divergence is noted here rather than left for the next reader to find (raised in this
 * PR's code review): if a third caller ever needs N actions, widen `EmptyState` and delete
 * this layout instead of copying it a second time.
 *
 * **Copy note (founder rules):** no em or en dashes, warm and plain, and it says what will
 * happen rather than what is being withheld. "We do not know which firm you mean" is the
 * honest but cold version; the reason we are asking is that showing one firm's families
 * under another firm's name would be wrong, and the sentence says that.
 */
export function AdvisorFirmChooser({ firms }: { firms: AdvisorFirmChoice }) {
  return (
    <div
      data-testid="advisor-firm-chooser"
      className="flex flex-col items-center gap-4 text-center py-10 px-4"
    >
      <Building2 size={20} strokeWidth={1.75} className="text-slate" aria-hidden />
      <h1 className="text-navy text-[15px] font-semibold font-sans m-0">
        Which firm are you working as?
      </h1>
      <p className="text-slate text-[13px] font-sans m-0 max-w-sm">
        Pick one, and this portal will show you that firm&rsquo;s clients only. You can
        change it at any time.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {firms.map(firm => (
          <button
            key={firm.firmId}
            type="button"
            onClick={() => setActingFirmId(firm.firmId)}
            className={btns.btnPrim}
          >
            {firm.name}
          </button>
        ))}
      </div>
    </div>
  );
}
